// Opt-in OTLP telemetry — boundary module facade (ADR-006).
//
// Every exported span helper accepts only structural, typed fields (tool
// name, duration via span start/end timestamps, token counts, model name,
// success/error class) — never a parameter shape that could carry free text
// (email subject/body, prompt/completion content). This is enforced by the
// function signatures themselves, not by a best-effort redaction pass, so
// there is no string field for that content to hide inside.
//
// No-ops entirely until `initTelemetry()` is awaited with telemetry enabled
// and a configured endpoint (see settings.ts `telemetryEnabled`/
// `telemetryEndpoint`/`telemetryToken`) — every helper below is safe to call
// unconditionally from agent-loop.ts/ai-client.ts; when telemetry is off they
// return `null` / do nothing.
//
// ── Why this file is a facade ─────────────────────────────────────────────
// The OTel Web SDK is ~52 KiB and telemetry is off by default, so it must not
// ship in the initial task pane chunk. It cannot simply be `import()`ed at the
// call site: agent-loop.ts and ai-client.ts need these helpers *synchronously*,
// mid-turn. So this module holds no `@opentelemetry/*` runtime import at all
// (the `Span` type below is `import type`, erased at compile time) and defers
// to ./telemetry-otel, which `initTelemetry()` loads dynamically.
//
// An earlier attempt put the `import()` in App.tsx while ai-client.ts and
// agent-loop.ts still imported this module's OTel-laden implementation
// statically — which meant webpack folded the "async" chunk straight back into
// the main one and nothing was ever deferred. Keep this file free of
// `@opentelemetry/*` runtime imports or that regression returns silently.

import type { Span } from "@opentelemetry/api";
import { AddinSettings } from "./settings";
import { debugLog } from "./debug";

/** A span handle from one of the `start*Span` helpers below. `null` when
 * telemetry is disabled/uninitialized — every `end*Span` helper accepts a
 * `null` handle as a no-op, so call sites never need to branch on whether
 * telemetry is active. */
export type TelemetrySpan = Span | null;

type TelemetryImpl = typeof import("./telemetry-otel");

let impl: TelemetryImpl | null = null;

export function isTelemetryInitialized(): boolean {
  return impl?.isTelemetryInitialized() ?? false;
}

/**
 * Loads the OTel implementation and initializes it, if and only if
 * `settings.telemetryEnabled` is true and `settings.telemetryEndpoint` is set.
 * When telemetry is off this returns without importing any OTel code at all —
 * that early return is what keeps the SDK off the wire for the default case.
 *
 * Idempotent and never throws: a failed chunk load or a misconfigured endpoint
 * degrades to "telemetry stays off" rather than breaking the add-in.
 *
 * Async (unlike the pre-facade version) because of the dynamic import. Spans
 * started before it resolves are `null`, so the first few may be dropped —
 * acceptable for opt-in telemetry, and preferable to blocking first render
 * against the ≤500 ms load target (B3).
 */
export async function initTelemetry(settings: AddinSettings): Promise<void> {
  if (impl) return;
  if (!settings.telemetryEnabled || !settings.telemetryEndpoint) return;

  try {
    const loaded = await import("./telemetry-otel");
    loaded.initTelemetry(settings);
    impl = loaded;
  } catch (e) {
    debugLog("telemetry:impl-load-failed", e instanceof Error ? e.name : "unknown");
    impl = null;
  }
}

/** Test-only: drops the loaded implementation and resets its global OTel
 * registration, so `initTelemetry()` can be re-exercised across test cases. */
export function resetTelemetryForTests(): void {
  impl?.resetTelemetryForTests();
  impl = null;
}

/** Starts a span covering one `runAgentLoop` turn (see agent-loop.ts). */
export function startAgentTurnSpan(): TelemetrySpan {
  return impl?.startAgentTurnSpan() ?? null;
}

export function endAgentTurnSpan(
  span: TelemetrySpan,
  attrs: { toolCallCount: number; success: boolean; errorClass?: string }
): void {
  impl?.endAgentTurnSpan(span, attrs);
}

/** Starts a span for one tool-call execution within an agent turn. `toolName`
 * must be an internal tool identifier (e.g. `"list_emails"`), never
 * user/email-derived content. */
export function startToolCallSpan(parent: TelemetrySpan, toolName: string): TelemetrySpan {
  return impl?.startToolCallSpan(parent, toolName) ?? null;
}

export function endToolCallSpan(
  span: TelemetrySpan,
  attrs: { success: boolean; errorClass?: string }
): void {
  impl?.endToolCallSpan(span, attrs);
}

/** Starts a span for one LLM provider call (see ai-client.ts `sendChat`/
 * `sendChatStream`). `model` is the configured model identifier, not
 * request/response content. */
export function startLlmCallSpan(
  parent: TelemetrySpan,
  attrs: { model: string; streaming: boolean }
): TelemetrySpan {
  return impl?.startLlmCallSpan(parent, attrs) ?? null;
}

export function endLlmCallSpan(
  span: TelemetrySpan,
  attrs: {
    success: boolean;
    inputTokens?: number;
    outputTokens?: number;
    errorClass?: string;
  }
): void {
  impl?.endLlmCallSpan(span, attrs);
}
